import { Injectable } from '@angular/core';
import { MockService } from '@mock/mock.service';
import { delay, Observable } from 'rxjs';
import { IApiResponse } from '@app/api/interfaces/common/api-response.interface';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import {
  ILoggerNoiseSpectrum,
  ILoggerNoiseSpectrumChartData,
  ILoggerNoiseSpectrumChartDataPoint,
} from '@app/api/interfaces/chart-data/logger-noise-spectrum';

@Injectable({ providedIn: 'root' })
export class LoggerNoiseSpectrumMockService extends MockService {
  public get(
    projectId: string,
    measurementId: string
  ): Observable<IApiResponse<ILoggerNoiseSpectrumChartData, object>> {
    return this.mockResponse(
      'LOGGER_NOISE_SPECTRUM:get',
      { data: { values: this.generate(measurementId) }, meta: {} },
      arguments
    ).pipe(delay(this.requestDelay));
  }

  private generate(measurementId: string): ILoggerNoiseSpectrumChartDataPoint[] {
    const frequencyStep = 5;
    const frequencyMin = 0;
    const frequencyMax = 600;

    const charSum = convertStringInToSumOfAllCharacters(measurementId);

    const noiseFloor = (charSum % 8) + 4;
    const noiseMax = noiseFloor * 4;

    const pickValue1 = Math.min(noiseFloor * ((charSum % 3) + 2), noiseMax);
    const pickValue2 = Math.min(noiseFloor * ((charSum % 2) + 2.5), noiseMax);
    const pickValue3 = Math.min(noiseFloor * ((charSum % 4) + 1.5), noiseMax);

    const segment = frequencyMax / 4;
    const pick1Freq = segment * 0.8 + (charSum % 60);
    const pick2Freq = segment * 2 + (charSum % 40);
    const pick3Freq = segment * 3.1 + (charSum % 30);

    const jaggedAmplitude = noiseFloor * 0.25;
    const jaggedPeriod = 5;
    const peakWidth = 18;

    const spectrum: ILoggerNoiseSpectrum[] = [];
    const pointsCount = Math.floor((frequencyMax - frequencyMin) / frequencyStep);

    for (let i = 0; i <= pointsCount; i++) {
      const currentFreq = frequencyMin + i * frequencyStep;
      let baseValue: number;

      if (currentFreq < 50) {
        const rampRatio = currentFreq / 50;
        baseValue = noiseMax * (1 - rampRatio) * 0.6 + noiseFloor;
      } else {
        const jaggedOffset = Math.sin((i / jaggedPeriod) * Math.PI) * jaggedAmplitude;
        baseValue = noiseFloor + jaggedOffset;
      }

      const p1 = pickValue1 * Math.exp(-Math.pow(currentFreq - pick1Freq, 2) / (2 * Math.pow(peakWidth, 2)));
      const p2 = pickValue2 * Math.exp(-Math.pow(currentFreq - pick2Freq, 2) / (2 * Math.pow(peakWidth, 2)));
      const p3 = pickValue3 * Math.exp(-Math.pow(currentFreq - pick3Freq, 2) / (2 * Math.pow(peakWidth, 2)));

      let finalValue = Math.max(baseValue, p1, p2, p3);
      finalValue = Math.min(finalValue, noiseMax);

      spectrum.push({
        frequency: currentFreq,
        spectrumDbValue: Number(finalValue.toFixed(6)),
      });
    }

    return spectrum;
  }
}
